import React, { useState } from 'react';
import {
  Undo,
  Redo,
  Save,
  FolderOpen,
  Settings,
  Grid3x3,
  RefreshCw,
  Trash2,
  Printer,
} from 'lucide-react';
import { TableConfig, TableSelection } from '@/app/types/table';
import { BorderStylePanel } from './BorderStylePanel';
import { useLanguage } from '@/app/contexts/LanguageContext';
import { RestrictedFeature } from '@/app/components/RestrictedFeature';

interface TableToolbarProps {
  tableConfig: TableConfig;
  selection: TableSelection | null;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onUpdateConfig: (config: TableConfig) => void;
  onSave?: () => void;
  onLoad?: () => void;
  onReloadValues?: () => void;
  onResetHeaders?: () => void;
  onPrint?: () => void;
}

export function TableToolbar({
  tableConfig,
  selection,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onUpdateConfig,
  onSave,
  onLoad,
  onReloadValues,
  onResetHeaders,
  onPrint,
}: TableToolbarProps) {
  const { t } = useLanguage();
  const [showBorderPanel, setShowBorderPanel] = useState(false);

  const hasSelection = selection !== null;

  const handleToggleGridLines = () => {
    const newConfig = {
      ...tableConfig,
      showGridLines: !tableConfig.showGridLines,
    };
    onUpdateConfig(newConfig);
  };

  return (
    <div
      className="flex items-center gap-2 px-4 py-2 border-b"
      style={{
        backgroundColor: 'var(--md-surface-container)',
        borderColor: 'var(--md-outline-variant)',
      }}
    >
      {/* Undo/Redo */}
      <div className="flex items-center gap-1 pr-2 border-r" style={{ borderColor: 'var(--md-outline-variant)' }}>
        <button
          onClick={onUndo}
          disabled={!canUndo}
          className="p-2 rounded hover:bg-opacity-10"
          style={{
            backgroundColor: canUndo ? 'transparent' : 'var(--md-surface-variant)',
            opacity: canUndo ? 1 : 0.5,
            cursor: canUndo ? 'pointer' : 'not-allowed',
          }}
          title={`${t('editor.actions.undo')} (Ctrl+Z)`}
        >
          <Undo className="w-5 h-5" />
        </button>
        <button
          onClick={onRedo}
          disabled={!canRedo}
          className="p-2 rounded hover:bg-opacity-10"
          style={{
            backgroundColor: canRedo ? 'transparent' : 'var(--md-surface-variant)',
            opacity: canRedo ? 1 : 0.5,
            cursor: canRedo ? 'pointer' : 'not-allowed',
          }}
          title={`${t('editor.actions.redo')} (Ctrl+Y)`}
        >
          <Redo className="w-5 h-5" />
        </button>
      </div>

      {/* Reload Values */}
      {onReloadValues && (
        <div className="flex items-center gap-1 pr-2 border-r" style={{ borderColor: 'var(--md-outline-variant)' }}>
          <button
            onClick={onReloadValues}
            className="p-2 rounded hover:bg-opacity-10 transition-colors"
            style={{
              backgroundColor: 'transparent',
            }}
            title={t('table.toolbar.reloadValues')}
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Reset Headers */}
      {onResetHeaders && (
        <div className="flex items-center gap-1 pr-2 border-r" style={{ borderColor: 'var(--md-outline-variant)' }}>
          <button
            onClick={() => {
              if (confirm(t('table.toolbar.resetHeadersConfirm'))) {
                onResetHeaders();
              }
            }}
            className="p-2 rounded hover:bg-opacity-10 transition-colors"
            style={{
              backgroundColor: 'transparent',
              color: '#d32f2f',
            }}
            title={t('table.toolbar.resetHeaders')}
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Styling */}
      <div className="flex items-center gap-1 pr-2 border-r" style={{ borderColor: 'var(--md-outline-variant)' }}>
        <button
          onClick={() => setShowBorderPanel(!showBorderPanel)}
          className="p-2 rounded hover:bg-opacity-10"
          title={t('table.toolbar.editBorders')}
        >
          <Grid3x3 className="w-5 h-5" />
        </button>
        <button
          onClick={handleToggleGridLines}
          className="p-2 rounded hover:bg-opacity-10"
          style={{
            backgroundColor: tableConfig.showGridLines ? 'var(--md-primary-container)' : 'transparent',
          }}
          title={t('table.toolbar.toggleGrid')}
        >
          <Settings className="w-5 h-5" />
        </button>
      </div>

      {/* Import/Export */}
      <div className="flex items-center gap-1">
        <button
          className="p-2 rounded hover:bg-opacity-10"
          title={t('table.toolbar.loadTemplate')}
          onClick={onLoad}
        >
          <FolderOpen className="w-5 h-5" />
        </button>
        <RestrictedFeature permission="export_bom">
          <button
            className="p-2 rounded hover:bg-opacity-10"
            title={t('common.export')}
            onClick={onSave}
          >
            <Save className="w-5 h-5" />
          </button>
        </RestrictedFeature>
        {onPrint && (
          <RestrictedFeature permission="export_pdf">
            <button
              className="p-2 rounded hover:bg-opacity-10"
              title={t('common.print')}
              onClick={onPrint}
            >
              <Printer className="w-5 h-5" />
            </button>
          </RestrictedFeature>
        )}
      </div>

      {/* Info */}
      <div className="ml-auto text-sm" style={{ color: 'var(--md-on-surface-variant)' }}>
        {tableConfig.rows.filter(r => !r.isHeader).length} {t('table.toolbar.dataRows')} × {tableConfig.columns.length} {t('table.toolbar.column')}
        {selection && (
          <span className="ml-2">
            | {t('table.toolbar.selection', { rows: selection.endRow - selection.startRow + 1, cols: selection.endCol - selection.startCol + 1 })}
          </span>
        )}
      </div>

      {/* Border Style Panel */}
      {showBorderPanel && (
        <BorderStylePanel
          selection={selection}
          tableConfig={tableConfig}
          onUpdateConfig={onUpdateConfig}
          onClose={() => setShowBorderPanel(false)}
        />
      )}
    </div>
  );
}
